import type {
	PrimitiveType,
	PrimitiveTypeAnyDateTime,
	PrimitiveTypeFloat,
	PrimitiveTypeInteger,
	PrimitiveTypeInteger64,
	PrimitiveTypeNumber,
	PrimitiveTypeSignedInteger,
	PrimitiveTypeStringEncodedNumber,
	PrimitiveTypeUnsignedInteger,
} from '#layers/data-model/types/schema/primitive-type';

export const PRIMITIVE_TYPE_INT8 = 'int8';
export const PRIMITIVE_TYPE_INT16 = 'int16';
export const PRIMITIVE_TYPE_INT32 = 'int32';
export const PRIMITIVE_TYPE_INT64 = 'int64';

export const PRIMITIVE_TYPES_SIGNED_INTEGER = [
	PRIMITIVE_TYPE_INT8,
	PRIMITIVE_TYPE_INT16,
	PRIMITIVE_TYPE_INT32,
	PRIMITIVE_TYPE_INT64,
] as const satisfies readonly PrimitiveTypeSignedInteger['name'][];

export const PRIMITIVE_TYPE_UINT8 = 'uint8';
export const PRIMITIVE_TYPE_UINT16 = 'uint16';
export const PRIMITIVE_TYPE_UINT32 = 'uint32';
export const PRIMITIVE_TYPE_UINT64 = 'uint64';

export const PRIMITIVE_TYPES_UNSIGNED_INTEGER = [
	PRIMITIVE_TYPE_UINT8,
	PRIMITIVE_TYPE_UINT16,
	PRIMITIVE_TYPE_UINT32,
	PRIMITIVE_TYPE_UINT64,
] as const satisfies readonly PrimitiveTypeUnsignedInteger['name'][];

export const PRIMITIVE_TYPES_INTEGER = [
	...PRIMITIVE_TYPES_SIGNED_INTEGER,
	...PRIMITIVE_TYPES_UNSIGNED_INTEGER,
] as const satisfies readonly PrimitiveTypeInteger['name'][];

/** 64-bit integers, carried as decimal strings on the wire and in memory. */
export const PRIMITIVE_TYPES_INTEGER_64 = [
	PRIMITIVE_TYPE_INT64,
	PRIMITIVE_TYPE_UINT64,
] as const satisfies readonly PrimitiveTypeInteger64['name'][];

export const INTEGER_64_BOUNDS = {
	[PRIMITIVE_TYPE_INT64]: { min: '-9223372036854775808', max: '9223372036854775807' },
	[PRIMITIVE_TYPE_UINT64]: { min: '0', max: '18446744073709551615' },
} as const satisfies Record<PrimitiveTypeInteger64['name'], { min: string; max: string }>;

/**
 * Integer types wide enough to hold a modern epoch-millisecond timestamp, so they can back a
 * datetime field. 32-bit integers are excluded: their whole range maps to ~50 days in 1970 when
 * read as epoch milliseconds.
 */
export const PRIMITIVE_TYPES_EPOCH_INTEGER = PRIMITIVE_TYPES_INTEGER_64;

/** Whether a primitive type name is a 64-bit integer usable as an epoch-millisecond timestamp. */
export function isEpochIntegerPrimitiveTypeName(name: string | null | undefined): boolean {
	return (PRIMITIVE_TYPES_EPOCH_INTEGER as readonly string[]).includes(name ?? '');
}

export const PRIMITIVE_TYPES_FLOAT = [
	'float32',
	'float64',
] as const satisfies readonly PrimitiveTypeFloat['name'][];

export const PRIMITIVE_TYPE_DECIMAL = 'decimal';

export const PRIMITIVE_TYPES_STRING_ENCODED_NUMBER = [
	...PRIMITIVE_TYPES_INTEGER_64,
	PRIMITIVE_TYPE_DECIMAL,
] as const satisfies readonly PrimitiveTypeStringEncodedNumber['name'][];

export const DecimalPrecisionKind = {
	Constrained: 'constrained',
	Unconstrained: 'unconstrained',
} as const;

export const PRIMITIVE_TYPES_NUMBER = [
	...PRIMITIVE_TYPES_INTEGER,
	...PRIMITIVE_TYPES_FLOAT,
	PRIMITIVE_TYPE_DECIMAL,
] as const satisfies readonly PrimitiveTypeNumber['name'][];

export const PRIMITIVE_TYPE_DATE = 'date';
export const PRIMITIVE_TYPE_TIME = 'time';
export const PRIMITIVE_TYPE_DATE_TIME = 'dateTime';
export const PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE = 'dateTimeWithTimezone';

export const PRIMITIVE_TYPES_DATETIME = [
	PRIMITIVE_TYPE_DATE,
	PRIMITIVE_TYPE_TIME,
	PRIMITIVE_TYPE_DATE_TIME,
	PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE,
] as const satisfies readonly PrimitiveTypeAnyDateTime['name'][];

export const PRIMITIVE_TYPE_UUID = 'uuid';
export const PRIMITIVE_TYPE_STRING = 'string';
export const PRIMITIVE_TYPE_JSON = 'json';
export const PRIMITIVE_TYPE_BOOLEAN = 'boolean';
export const PRIMITIVE_TYPE_BYTES = 'bytes';
export const PRIMITIVE_TYPE_GEOMETRY = 'geometry';
export const PRIMITIVE_TYPE_UNSUPPORTED = 'unsupported';

export const SUPPORTED_PRIMITIVE_TYPES = [
	...PRIMITIVE_TYPES_NUMBER,
	...PRIMITIVE_TYPES_DATETIME,
	PRIMITIVE_TYPE_JSON,
	PRIMITIVE_TYPE_BOOLEAN,
	PRIMITIVE_TYPE_UUID,
	PRIMITIVE_TYPE_STRING,
	PRIMITIVE_TYPE_BYTES,
	PRIMITIVE_TYPE_GEOMETRY,
] as const satisfies readonly PrimitiveType['name'][];

export const PRIMITIVE_TYPES = [
	...SUPPORTED_PRIMITIVE_TYPES,
	PRIMITIVE_TYPE_UNSUPPORTED,
] as const satisfies readonly PrimitiveType['name'][];
